import { useMemo } from 'react';

import sortBy from 'lodash-es/sortBy';
import { CheckIcon, InfoIcon, XIcon } from 'lucide-react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import CardBase from '@/components/organisms/Integrations/Components/CardBase';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Autocomplete } from '@/components/ui/custom/Autocomplete/Autocomplete';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { InputGroup, InputGroupAddon, InputGroupInput } from '@/components/ui/input-group';
import { Spinner } from '@/components/ui/spinner';
import { useTelnyxVoice } from '@/hooks/useTelnyxVoice';
import {
  IntegrationStatus,
  MergedTelnyxNumber,
  TelnyxVoiceIntegration,
  VoiceCredentialStatus,
} from '@/shared/types/integration';
import { getFlagByPhoneNumber } from '@/util/util';

const Account = ({ integration }: { integration: TelnyxVoiceIntegration }) => {
  const { t } = useTranslation();
  const { control, setValue } = useFormContext<TelnyxVoiceIntegration>();
  const { apiKeyStatus, phones, verificationStatus } = useTelnyxVoice(integration.integration_id);

  // API key can't be updated after save — integrations-controller ignores the change
  const isApiKeySaved = !!integration.config.api_key;

  const selectedPhone = useWatch({ control, name: 'config.phone_number' });
  const selectedKind = useWatch({ control, name: 'config.phone_number_kind' });

  const handlePhoneSelect = (value: string) => {
    const selected = phones?.find((p) => p.phone_number === value);
    setValue('config.phone_number', value, { shouldDirty: true });
    setValue('config.phone_number_kind', selected?.kind, { shouldDirty: true });
    // Verified caller IDs are not Telnyx-owned and never carry a phone_number_id.
    // Clear any stale ID from a previous native pick within this form session.
    // For native picks we leave phone_number_id alone — IC fills it during verify.
    if (selected?.kind === 'verified') {
      setValue('config.phone_number_id', undefined, { shouldDirty: true });
    } else {
      // Emit '' (not undefined) on native pick. undefined is dropped by
      // removeEmptyFieldsDeep before reaching orch; '' rides through the
      // CONFIG_KEYS_TO_SKIP allowlist and clears the stored field. Clear the
      // whole BYOC egress group together so no orphaned SIP-auth creds persist.
      setValue('config.egress_sip_address', '', { shouldDirty: true });
      setValue('config.egress_sip_auth_username', '', { shouldDirty: true });
      setValue('config.egress_sip_auth_password', '', { shouldDirty: true });
    }
  };

  const phoneOptions = useMemo(() => {
    const nativeAvailable: MergedTelnyxNumber[] = [];
    const nativeUnavailable: MergedTelnyxNumber[] = [];
    const verifiedPhones: MergedTelnyxNumber[] = [];

    phones?.forEach((phone) => {
      const isCurrentPhone = phone.phone_number === selectedPhone;
      if (phone.kind === 'verified') {
        // Verified CIDs share the Telnyx universe across Moveo accounts.
        // If another telnyx-voice integration already holds this number, the
        // orchestrator's unique index would 409 on save, so bucket it under
        // "In use" (disabled) to surface the conflict at picker time instead.
        if (isCurrentPhone || !phone.in_use) {
          verifiedPhones.push(phone);
        } else {
          nativeUnavailable.push(phone);
        }
        return;
      }
      if (isCurrentPhone || !phone.connection_id) {
        nativeAvailable.push(phone);
      } else {
        nativeUnavailable.push(phone);
      }
    });

    const labelFor = (phone: MergedTelnyxNumber) => {
      const flag = getFlagByPhoneNumber(phone.phone_number);
      return flag ? `${flag} ${phone.phone_number}` : phone.phone_number;
    };

    const sortedNativeAvailable = sortBy(
      nativeAvailable.map((phone) => ({
        value: phone.phone_number,
        label: labelFor(phone),
        category: t('common.available'),
      })),
      [(option) => (option.value === selectedPhone ? 0 : 1), 'label']
    );

    const sortedVerified = sortBy(
      verifiedPhones.map((phone) => ({
        value: phone.phone_number,
        label: labelFor(phone),
        category: t('integrations.voice_common.verified_caller_id'),
      })),
      [(option) => (option.value === selectedPhone ? 0 : 1), 'label']
    );

    const verifiedTag = t('integrations.voice_common.verified_caller_id');
    const sortedNativeUnavailable = sortBy(
      nativeUnavailable.map((phone) => ({
        value: phone.phone_number,
        // Tag verified CIDs so they're distinguishable from native numbers in
        // the shared "In use" bucket. The dedicated "Verified caller ID" group
        // header already conveys this when the entry is available.
        label: phone.kind === 'verified' ? `${labelFor(phone)} · ${verifiedTag}` : labelFor(phone),
        disabled: true,
        category: t('integrations.voice_common.phone_number_in_use'),
      })),
      ['label']
    );

    return [
      { label: t('common.none'), value: '' },
      ...sortedNativeAvailable,
      ...sortedVerified,
      ...sortedNativeUnavailable,
    ];
  }, [phones, selectedPhone, t]);

  const apiKeyValidationAddon = useMemo(() => {
    if (apiKeyStatus === VoiceCredentialStatus.VALIDATING) {
      return <Spinner aria-label={t('common.validating')} />;
    }
    if (apiKeyStatus === VoiceCredentialStatus.VERIFIED) {
      return <CheckIcon className="size-4 text-successful" />;
    }
    if (apiKeyStatus === VoiceCredentialStatus.INVALID) {
      return <XIcon className="size-4 text-destructive" />;
    }
    return null;
  }, [apiKeyStatus, t]);

  return (
    <CardBase
      title={t('common.account')}
      status={
        verificationStatus === VoiceCredentialStatus.VERIFIED
          ? IntegrationStatus.VERIFIED
          : integration.status
      }
    >
      <FormField
        control={control}
        name="config.api_key"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('integrations.telnyx_voice_connect.api_key.title')}</FormLabel>
            <FormControl>
              <InputGroup>
                <InputGroupInput
                  {...field}
                  value={field.value || ''}
                  type="password"
                  disabled={isApiKeySaved}
                  placeholder={t('integrations.telnyx_voice_connect.api_key.placeholder')}
                  onChange={(e) => field.onChange(e.target.value.trim())}
                />
                {!isApiKeySaved && (
                  <InputGroupAddon align="inline-end">{apiKeyValidationAddon}</InputGroupAddon>
                )}
              </InputGroup>
            </FormControl>
            <FormDescription>
              {isApiKeySaved
                ? t('integrations.telnyx_voice_connect.api_key.saved_description')
                : t('integrations.telnyx_voice_connect.api_key.description')}
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="config.public_key"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('integrations.telnyx_voice_connect.public_key.title')}</FormLabel>
            <FormControl>
              <Input
                {...field}
                value={field.value || ''}
                type="text"
                placeholder={t('integrations.telnyx_voice_connect.public_key.placeholder')}
                onChange={(e) => field.onChange(e.target.value.trim())}
              />
            </FormControl>
            <FormDescription>
              {t('integrations.telnyx_voice_connect.public_key.description')}
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />

      {verificationStatus === VoiceCredentialStatus.VERIFIED && (
        <FormField
          control={control}
          name="config.phone_number"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('field.phone')}</FormLabel>
              <FormControl>
                <Autocomplete
                  options={phoneOptions}
                  value={field.value}
                  onChange={handlePhoneSelect}
                />
              </FormControl>
              <FormDescription>
                {t('integrations.telnyx_voice_connect.phone_number_description')}
              </FormDescription>
              {selectedKind === 'verified' && (
                <Alert variant="info" className="mt-2">
                  <InfoIcon />
                  <AlertTitle>
                    {t('integrations.telnyx_voice_connect.verified_caller_id_disclaimer_title')}
                  </AlertTitle>
                  <AlertDescription>
                    {t('integrations.telnyx_voice_connect.verified_caller_id_disclaimer_body')}
                  </AlertDescription>
                </Alert>
              )}
            </FormItem>
          )}
        />
      )}

      {verificationStatus === VoiceCredentialStatus.VERIFIED && selectedKind === 'verified' && (
        <FormField
          control={control}
          name="config.egress_sip_address"
          render={({ field }) => {
            const showPrefixWarning = /^sips?:/i.test(field.value || '');
            return (
              <FormItem>
                <FormLabel>
                  {t('integrations.telnyx_voice_connect.egress_sip_address.label')}
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    value={field.value || ''}
                    type="text"
                    placeholder={t(
                      'integrations.telnyx_voice_connect.egress_sip_address.placeholder'
                    )}
                    onChange={(e) => field.onChange(e.target.value.trim())}
                  />
                </FormControl>
                <FormDescription>
                  {t('integrations.telnyx_voice_connect.egress_sip_address.description')}
                </FormDescription>
                {showPrefixWarning && (
                  <p className="text-sm text-warning">
                    {t('integrations.telnyx_voice_connect.egress_sip_address.prefix_warning')}
                  </p>
                )}
                <FormMessage />
              </FormItem>
            );
          }}
        />
      )}

      {verificationStatus === VoiceCredentialStatus.VERIFIED && selectedKind === 'verified' && (
        <FormField
          control={control}
          name="config.egress_sip_auth_username"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t('integrations.telnyx_voice_connect.egress_sip_auth_username.label')}
              </FormLabel>
              <FormControl>
                <Input
                  {...field}
                  value={field.value || ''}
                  type="text"
                  placeholder={t(
                    'integrations.telnyx_voice_connect.egress_sip_auth_username.placeholder'
                  )}
                  onChange={(e) => field.onChange(e.target.value.trim())}
                />
              </FormControl>
              <FormDescription>
                {t('integrations.telnyx_voice_connect.egress_sip_auth.description')}
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
      )}

      {verificationStatus === VoiceCredentialStatus.VERIFIED && selectedKind === 'verified' && (
        <FormField
          control={control}
          name="config.egress_sip_auth_password"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t('integrations.telnyx_voice_connect.egress_sip_auth_password.label')}
              </FormLabel>
              <FormControl>
                <Input
                  {...field}
                  value={field.value || ''}
                  type="password"
                  placeholder={t(
                    'integrations.telnyx_voice_connect.egress_sip_auth_password.placeholder'
                  )}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      )}
    </CardBase>
  );
};

export default Account;
